/**
 * Component TabControl
 *
 * @author John Montoya
 * @author Dahiana Tamayo
 * @description The TabControl component handles the rendering of tabs with optional permissions and state persistence.
 *
 * @param {Object} props - Props of the component
 * @param {Array} props.config - Array of tab configurations containing label, component, and optional permissions.
 * @param {number} props.selected - Index of the initially selected tab.
 * @param {boolean} props.keepSelected - Boolean to determine if the selected tab should be saved in cookies.
 * @param {Function} props.setSelected - Function to be called when a new tab is selected.
 * @param {boolean} props.loading - Boolean indicating whether the component is in a loading state.
 * @param {Object} props.reference - Reference to control the state of the component externally.
 * @param {Function} props.checkAccess -Function to check user access for a particular tab based on permissions.
 *
 * @example 
 * <TabControl config={[{label: 'Tab', component: <Component />, permissions: ['permission']}]} selected={0} />
 */

import React from 'react';
import {
  Tabs,
  Tab,
  Box,
  Paper,
  AppBar,
  Divider,
  withStyles,
  Typography,
} from '@material-ui/core';
import Cookie from 'js-cookie';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import Loader from '../LoadingScreen';
import { HelpCircle } from 'react-feather';
import { withPermissionsChecker } from 'wrappers';
import { Alert } from '@material-ui/lab';

/**
 * the props a receive are
 * is required: config = [
    { label: 'Tab 1', component: <TabBody id={id} /> },
    { label: 'Tab 2', component: <TabBody id="1" /> },
  ];
  * is optional: selected = 1
 */

const styles = ((theme) => ({
  tab: {
    '& .MuiTab-wrapper': {
      display: 'flex',
      flexDirection: 'row-reverse',
    },
  },
  tabSize: {
    [theme.breakpoints.down('sm')]: {
      maxWidth: window.screen.width - 30,
    },
  },
}));

class TabControl extends React.Component
{
  constructor(props)
  {
    super(props);

    const { selected, reference } = props;

    // Initialize the state with the selected tab and visibility settings.
    this.state = {
      selectedTab: selected,
      visible: true,
    };
    // Set the external reference to allow state manipulation from outside.
    if (reference)
    {
      reference.current = {
        setState: (newState) => this.setState(newState),
      };
    }
  }

  componentDidMount()
  {
    const {
      config,
      selected,
      keepSelected,
      location,
    } = this.props;
    const { pathname } = location;
    // Restore selected tab from cookies if keepSelected is enabled.
    if (keepSelected)
    {
      const cookieSelected = Cookie.get('selectedTab');
      if (this.checkJson(cookieSelected))
      {
        const parsed = JSON.parse(cookieSelected);
        const { selected: cSelected, view } = parsed;
        if (view === pathname)
        {
          this.setState({ selectedTab: cSelected });
        }
      }
    }
    if (config[selected].invisible)
    {
      // eslint-disable-next-line react/no-did-update-set-state
      let bool = false;
      config.forEach((item, index) =>
      {
        if (bool)
        {
          return;
        }
        if (!item.invisible)
        {
          this.setState({
            selectedTab: index,
            visible: true,
          });
          bool = true;
        }
      });
      if (!bool)
      {
        this.setState({
          visible: false,
        });
      }
    }
  }

  componentDidUpdate(prevProps)
  {
    const {
      loading, config, selected,
    } = this.props;

    // Update selected tab if props have changed.
    if (loading !== prevProps.loading)
    {
      if (config[selected].invisible)
      {
        // eslint-disable-next-line react/no-did-update-set-state
        let bool = false;
        config.forEach((item, index) =>
        {
          if (bool)
          {
            return;
          }
          if (!item.invisible)
          {
            this.setState({
              selectedTab: index,
              visible: true,
            });
            bool = true;
          }
        });
        if (!bool)
        {
          this.setState({
            visible: false,
          });
        }
      }
    }
    if (selected !== prevProps.selected)
    {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ selectedTab: selected });
    }
    if (prevProps.config[selected].invisible !== config[selected].invisible)
    {
      // eslint-disable-next-line react/no-did-update-set-state
      let bool = false;
      config.forEach((item, index) =>
      {
        if (bool)
        {
          return;
        }
        if (!item.invisible)
        {
          this.setState({
            selectedTab: index,
            visible: true,
          });
          bool = true;
        }
      });
      if (!bool)
      {
        this.setState({
          visible: false,
        });
      }
    }
  }

  checkJson = (something) =>
  {
    try
    {
      JSON.parse(something);
      return true;
    }
    catch (e)
    {
      return false;
    }
  }

  // change the selected tab in the state
  handleChange = (event, nextSelectedTab) =>
  {
    const { selectedTab } = this.state;
    const { setSelected, keepSelected, location } = this.props;
    const { pathname } = location;
    clearTimeout(this.timer);
    if (selectedTab !== nextSelectedTab)
    {
      this.setState({ selectedTab: nextSelectedTab });

      if (keepSelected)
      {
        Cookie.set(
          'selectedTab',
          JSON.stringify({ selected: nextSelectedTab, view: pathname }),
        );
      }

      if (setSelected)
      {
        setSelected(nextSelectedTab);
      }
    }
  };

  render()
  {
    const {
      visible,
      selectedTab,
    } = this.state;
    const {
      config,
      loading,
      classes,
      checkAccess,
    } = this.props;

    // Filter visible tabs and those with allowed access
    const visibleTabs = config.filter((element, _index) => {
      if (element.invisible) return false;
      if (element.permission && !checkAccess(element.permission)) return false;
      return true;
    });

    // Determine the index of the selected tab within the visible tabs
    const visibleTabIndex = visibleTabs.findIndex((_tab, index) => index === selectedTab);

    // The component should be based on the index of the visible tab
    const tabConfig = visibleTabs[visibleTabIndex] || null;
    const { component: Component, wrap = true } = tabConfig || {};

    // Check if there is at least one visible tab
    const hasVisibleTab = visibleTabs.length > 0;

    return loading ? <Loader /> : (
      <>
        {visible && (
        <>
          <AppBar position="static" color="inherit" className={classes.tabSize}>
            <Tabs
              indicatorColor="primary"
              textColor="primary"
              value={selectedTab}
              onChange={this.handleChange}
              variant="scrollable"
            >
              {visibleTabs.map((element, index) => (
                <Tab
                  key={`tab${String(index)}`}
                  label={element.label}
                  icon={
                    element.actions && Object.keys(element.actions).length > 0 ? (
                      <HelpCircle
                        style={{ marginTop: 5, marginLeft: 10 }}
                        onMouseEnter={e => element.actions.openPopover(e, element.actions.message)}
                        onMouseLeave={element.actions.closePopover}
                        onMouseDown={e => element.actions.openPopover(e, element.actions.message)}
                      />
                    ) : null
                  }
                  className={classes.tab}
                  value={index}
                  style={{ textTransform: 'none', backgroundColor: element.color || '' }}
                  disabled={element.disabled ? element.disabled : false}
                />
              ))}
            </Tabs>
          </AppBar>
          {hasVisibleTab ? (
            wrap ? (
              <Paper>
                <Divider />
                <Typography component="div" role="tabpanel">
                  <Box p={3}>{Component}</Box>
                </Typography>
              </Paper>
            ) : (
                Component
              )
          ) : (
              <Paper style={{ padding: 10 }}>
                <Divider />
                <Alert severity="info">No tienes permisos para este tab</Alert>
              </Paper>
            )}
        </>
        )}
      </>
    );
  }
}

TabControl.propTypes = {
  config: PropTypes.oneOfType([PropTypes.array]).isRequired,
  selected: PropTypes.number,
  loading: PropTypes.bool,
  setSelected: PropTypes.func,
  keepSelected: PropTypes.bool,
  checkAccess: PropTypes.func,
  classes: PropTypes.oneOfType([PropTypes.object]),
  location: PropTypes.oneOfType([PropTypes.object]),
  reference: PropTypes.oneOfType([PropTypes.object]),
};

TabControl.defaultProps = {
  selected: 0,
  keepSelected: false,
};

export default withRouter(withStyles(styles)(withPermissionsChecker(TabControl)));
